
import { useRef, useState, useMemo, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { OWNER_STATUSES } from 'constants/statuses'

import type { Owner, GetOwnersPayload, OwnerSortField, SortOrder, OwnerGender, OwnerStatus } from 'store/owners/types'
import { capitalize, date, TRANSFER_PERMISSION_TOOLTIP } from 'utils'

import { useCanAccess, useCurrentCompany, useReferrerState } from 'hooks'
import { useOwners, useShopifyConnectionStatus, useResendOwnerInvitation } from 'hooks/api'
import { useSnackbar } from 'contexts'
import { createApiCall } from 'services'
import { UsersIcon, TrashIcon, PencilIcon, InfoIcon, ArrowLeftRightIcon, PlusIcon, UserPlusIcon, ShoppingBagIcon, MailIcon, ArchiveIcon, ArchiveRestoreIcon, DownloadIcon } from 'lucide-react'
import { ROUTES } from 'routes'

import { Box, Tooltip, Stack, FormControlLabel, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { Button, Switch, Tabs, TABS_CONTENT_GAP } from '@repo/ui'
import {
  Header,
  DeleteOwnerModal,
  DeleteOwnersModal,
  ArchiveOwnerModal,
  TransferCertificateModal,
  EmptyState,
  AskAboutButton,
  ActionDropdown,
  UserCell,
  Table,
  StatusChipCell,
  AddOwnerModal,
  AggregateCountCell,
  ToggleField,
  type ActionDropdownItem,
  IntroDataBanner,
} from 'components'
import { LeadsTab, type LeadsTabHandle } from './components'

// VERA-914 / VRG-105 (Stage B): the "People" surface — Owners | Leads tabs, at
// /people. `/owners` (and its query, e.g. the Products → Owners deep link
// `?filters=product:isAnyOf:<id>`) redirects to /people in App.tsx; the Owners tab
// is the default so that deep link still lands correctly.
const PEOPLE_TAB_VALUES = ['owners', 'leads'] as const
type PeopleTab = (typeof PEOPLE_TAB_VALUES)[number]

type Filters = GetOwnersPayload

const DEFAULT_FILTERS: Filters = {
  page: 1,
  size: 20,
  sort: 'registrationDate' as OwnerSortField,
  order: 'desc' as SortOrder,
}

// MERGE-KEEP(VERA-455): cross-reference deep-link support (Products → Owners). Keep
// on conflict with VERA-914. Extracts a reference id from the shared Table's
// `?filters=field:op:value` param (e.g. `product:isAnyOf:<id>`).
const parseReferenceFilter = (filterParam: string | null, field: string): string | undefined => {
  if (!filterParam) return undefined
  for (const clause of filterParam.split('&')) {
    const [f, , value] = clause.split(':')
    if (f === field) return value || undefined
  }
  return undefined
}

export const PeoplePage = () => {
  const navigate = useNavigate()
  // Hands "← Back to People" (plus this list's filters/sort) to every detail page linked from here.
  const referrer = useReferrerState()
  const [searchParams, setSearchParams] = useSearchParams()
  const [paginationModel, setPaginationModel] = useState({
    page: DEFAULT_FILTERS.page! - 1,
    pageSize: DEFAULT_FILTERS.size!
  })

  // VERA-914 / VRG-105 (Stage B): Owners | Leads tab, reflected in the URL
  // (?tab=) so it's deep-linkable + back/forward friendly (mirrors CompanyPage).
  // Owners stays the default so the existing Products → Owners deep link
  // (?filters=product:isAnyOf:<id>, no ?tab=) still lands on the Owners tab.
  const tabParam = searchParams.get('tab') as PeopleTab | null
  const tab: PeopleTab = tabParam && PEOPLE_TAB_VALUES.includes(tabParam) ? tabParam : 'owners'
  const setTab = (next: PeopleTab) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next === 'owners') {
          params.delete('tab')
        } else {
          params.set('tab', next)
        }
        return params
      },
      { replace: true }
    )
  }

  const { isHasAccess: isCanTransfer } = useCanAccess({ action: 'transfer_certificate' })
  const { open: notification } = useSnackbar()

  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openBulkDeleteModal, setOpenBulkDeleteModal] = useState(false)
  const [openArchiveModal, setOpenArchiveModal] = useState(false)
  const [isUnarchiving, setIsUnarchiving] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null)
  const [selectedOwners, setSelectedOwners] = useState<Owner[]>([])
  const [openTransferModal, setOpenTransferModal] = useState(false)
  const [openAddModal, setOpenAddModal] = useState(false)
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([])
  const [showArchived, setShowArchived] = useState(false)
  // Draft value for the Filters side panel — committed to showArchived only on Apply.
  const [showArchivedDraft, setShowArchivedDraft] = useState(false)
  // VERA-914 / VRG-105 (Stage B) — imperative handle so the shared Header's
  // "Export" button (Leads tab only) can trigger LeadsTab's CSV export.
  const leadsTabRef = useRef<LeadsTabHandle>(null)

  // Shopify integration
  const { data: connectionStatus, isLoading: isLoadingConnection } = useShopifyConnectionStatus()
  const isShopifyConnected = connectionStatus?.connected === true
  const { company } = useCurrentCompany()

  // Build filters from URL params
  const filters = useMemo((): Filters => {
    return {
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
      q: searchParams.get('q') || undefined,
      sort: (searchParams.get('sort') as OwnerSortField) || DEFAULT_FILTERS.sort,
      order: (searchParams.get('order') as SortOrder) || DEFAULT_FILTERS.order,
      status: (searchParams.get('status') as OwnerStatus) || undefined,
      // MERGE-KEEP(VERA-455): Products → Owners reference filter.
      productId: parseReferenceFilter(searchParams.get('filters'), 'product'),
      gender: (searchParams.get('gender') as OwnerGender) || undefined,
      ageMin: searchParams.get('ageMin') ? Number(searchParams.get('ageMin')) : undefined,
      ageMax: searchParams.get('ageMax') ? Number(searchParams.get('ageMax')) : undefined,
      registeredFrom: searchParams.get('registeredFrom') || undefined,
      registeredTo: searchParams.get('registeredTo') || undefined,
      showArchived: showArchived || undefined,
    }
  }, [searchParams, paginationModel, showArchived])

  const { data, isLoading, refetch } = useOwners(filters)
  const { mutate: resendInvitation, isPending: isResendingInvitation } = useResendOwnerInvitation()

  // VRG-227 — Download used to export only the loaded page. Re-issues the SAME `filters`
  // `useOwners` above already drives (search/status/gender/age/date-range filters), just
  // with a different page/size, so Table can walk every matching row for export. The
  // owners endpoint paginates by 1-based `page` + `size`, not limit/offset — translated
  // here so `Table` (and its two other callers) can stay on one limit/offset shape.
  // Called directly via `createApiCall` rather than `useOwners`'s redux action, which
  // writes into a single shared `state.owners.owners` slice an export paging loop would
  // otherwise clobber mid-fetch.
  const fetchExportPage = useCallback(async ({ limit, offset }: { limit: number; offset: number }) => {
    const response = await createApiCall({
      method: 'GET',
      url: '/owners',
      params: {
        ...filters,
        page: Math.floor(offset / limit) + 1,
        size: limit,
      },
    })
    const body = response.data as { items: Owner[]; totalItems: number }
    return { rows: body?.items ?? [], total: body?.totalItems ?? 0 }
  }, [filters])

  const owners = data?.items || []
  const total = data?.totalItems || 0

  const searchValue = searchParams.get('q') || ''

  const handlePaginationModelChange = (newModel: { page: number; pageSize: number }) => {
    if (newModel.pageSize !== paginationModel.pageSize) {
      setPaginationModel({ page: 0, pageSize: newModel.pageSize })
    } else {
      setPaginationModel(newModel)
    }
  }

  type Row = (typeof owners)[number]

  const columns = useMemo<GridColDef<Row>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Owner',
        type: 'string',
        pinned: 'left',
        disableReorder: true,
        width: 250,
        renderCell: (params: GridRenderCellParams<Row>) => {
          return <UserCell user={params.row} route={ROUTES.OWNER(params.row.id)} />
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'singleSelect',
        valueOptions: ['active', 'inactive', 'archived'],
        width: 110,
        align: 'center',
        renderCell: ({ row }) => {
          const status = row.archived ? 'archived' : row.status
          return <StatusChipCell status={status} statuses={OWNER_STATUSES} />
        },
      },
      {
        field: 'certificates',
        headerName: 'Certificates',
        type: 'number',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        // VERA-914 / VRG-105 (Stage B) — aggregate-reference cell: the count links to
        // this owner's certificates on the Certificates page, mirroring the Products →
        // Certificates deep link (`?filters=product:isAnyOf:<id>`).
        renderCell: (params: GridRenderCellParams<Row>) => (
          <AggregateCountCell
            backLabel="Owners"
            count={params.row.certificates}
            to={`${ROUTES.CERTIFICATES}?filters=owner:isAnyOf:${encodeURIComponent(params.row.id)}`}
          />
        ),
      },
      {
        field: 'products_count',
        headerName: 'Products',
        type: 'number',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        // VRG-80 (VERA-455) — aggregate-reference cell: distinct products across this
        // owner's certificates; the count links to those products on the Products page
        // (`/products?filters=owner:isAnyOf:<id>`), mirroring the Certificates cell above.
        renderCell: (params: GridRenderCellParams<Row>) => (
          <AggregateCountCell
            backLabel="Owners"
            count={params.row.products_count}
            to={`${ROUTES.PRODUCTS}?filters=owner:isAnyOf:${encodeURIComponent(params.row.id)}`}
          />
        ),
      },
      {
        field: 'transfers',
        headerName: 'Transfers',
        type: 'number',
        width: 120,
      },
      {
        field: 'scans',
        headerName: 'Scans',
        type: 'number',
        width: 100,
      },
      {
        field: 'leads',
        headerName: 'Leads',
        type: 'number',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        // VERA-914 / VRG-105 (Stage B) — aggregate-reference cell: the count links
        // to this owner's leads on the Leads tab (?tab=leads&referrer=<ownerId>).
        renderCell: (params: GridRenderCellParams<Row>) => (
          <AggregateCountCell
            backLabel="Owners"
            count={params.row.leads}
            to={`${ROUTES.OWNERS}?tab=leads&referrer=${encodeURIComponent(params.row.id)}`}
          />
        ),
      },
      {
        field: 'registrationDate',
        headerName: 'Join Date',
        type: 'dateTime',
        width: 180,
        valueGetter: (value: string | Date) => value && new Date(value),
        renderCell: ({ value }) => value ? date(value) : '-',
      },
      {
        field: 'age',
        headerName: 'Age',
        type: 'number',
        width: 80,
        renderCell: ({ value }) => value || '-',
      },
      {
        field: 'gender',
        headerName: 'Gender',
        type: 'singleSelect',
        valueOptions: ['Male', 'Female', 'Other', 'PreferNotToSay'],
        width: 100,
        renderCell: ({ value }) => value ? capitalize(value) : '-',
      },
      {
        field: 'email',
        headerName: 'Email',
        type: 'string',
        width: 200,
        renderCell: ({ value }) => (
          <Tooltip title={value} placement="top">
            <span style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {value}
            </span>
          </Tooltip>
        ),
      },
      {
        field: 'phone',
        headerName: 'Phone',
        type: 'string',
        width: 150,
        renderCell: ({ value }) => value ? (
          <Tooltip title={value} placement="top">
            <span style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {value}
            </span>
          </Tooltip>
        ) : '-',
      },
      {
        field: 'integrationSource',
        headerName: 'Source',
        type: 'singleSelect',
        valueOptions: ['SHOPIFY', 'MANUAL', 'API'],
        width: 100,
        renderCell: ({ value }) => {
          if (!value || value === 'MANUAL') return '-';
          return (
            <Tooltip title={`Imported from ${value}`} placement="top">
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: value === 'SHOPIFY' ? '#8B5CF6' : '#6B7280',
              }}>
                {value === 'SHOPIFY' && <ShoppingBagIcon size={12} style={{ marginRight: 4 }} />}
                {value}
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: 'actions',
        headerName: ' ',
        sortable: false,
        align: 'center',
        width: 54,
        pinned: 'right',
        renderCell: ({ row }) => <ActionDropdown items={getActionItems(row)} />,
      },
    ],
    [showArchived]
  )

  const handleViewOwner = (id: string) => {
    navigate(id, { state: referrer })
  }

  const handleEditOwner = (id: string) => {
    navigate(ROUTES.OWNER_EDIT(id), { state: referrer })
  }

  const handleDeleteOwner = (row: Owner) => {
    setOpenDeleteModal(true)
    setSelectedOwner(row)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setSelectedOwner(null)
  }

  const handleOpenArchiveModal = (row: Owner, unarchiving = false) => {
    setSelectedOwner(row)
    setIsUnarchiving(unarchiving)
    setOpenArchiveModal(true)
  }

  const handleCloseArchiveModal = () => {
    setOpenArchiveModal(false)
    setSelectedOwner(null)
    setIsUnarchiving(false)
  }

  const handleTransferCertificate = (row: Owner) => {
    setSelectedOwner(row)
    setOpenTransferModal(true)
  }

  const handleCloseTransferModal = () => {
    setSelectedOwner(null)
    setOpenTransferModal(false)
  }

  const handleBulkDeleteOwners = (owners: Owner[]) => {
    setSelectedOwners(owners)
    setOpenBulkDeleteModal(true)
  }

  const handleCloseBulkDeleteModal = () => {
    setSelectedOwners([])
    setOpenBulkDeleteModal(false)
    setRowSelectionModel([])
  }

  const handleOwnersDeleted = () => {
    refetch()
    setRowSelectionModel([])
  }

  const ownerToCustomer = (owner: Owner) => {
    const nameParts = owner.name.split(' ')
    return {
      id: owner.id,
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      email: owner.email,
      username: '',
      birthday: null,
      avatar: owner.avatar,
      phone: owner.phone,
      otp_reference: '',
      active: owner.status === 'active',
      gender: owner.gender,
      country: '',
      created_at: owner.registrationDate,
      updated_at: owner.registrationDate,
      scans_count: owner.scans,
      certificates_count: owner.certificates,
      // VRG-416 — no directional split here, deliberately. The owners LIST
      // endpoint returns a single `transfers` total and no direction, so
      // transfers_in_count / transfers_out_count are LEFT UNDEFINED rather
      // than invented. They used to be `Math.floor(owner.transfers / 2)` /
      // `Math.ceil(...)` — one real number halved to manufacture two, which
      // reported an owner with 7 transfers as 3 in / 4 out regardless of what
      // happened. A blank is honest; a halved total is not.
      //
      // The real fix is server-side: port the two COUNT subqueries the DETAIL
      // endpoint already runs into the list query
      // (`owner-company.drizzle.repository.ts`, where transfers_count is
      // currently a hardcoded `0 // TODO`).
      transfers_count: owner.transfers,
    }
  }

  const handleCreateOwner = () => {
    setOpenAddModal(true)
  }

  const handleCloseAddModal = () => {
    setOpenAddModal(false)
  }

  const handleResendInvite = (row: Owner) => {
    resendInvitation(
      { ownerId: row.id },
      {
        onSuccess: (data) => {
          notification({
            type: 'success',
            text: `Invitation sent to ${data.email}`,
          })
          refetch()
        },
        onError: (error) => {
          notification({
            type: 'error',
            text: error.message || 'Failed to send invitation',
          })
        },
      }
    )
  }

  const getActionItems = useCallback(
    (row: Owner): ActionDropdownItem[] => {
      const isArchived = !!row.archived
      const items: ActionDropdownItem[] = [
        {
          label: 'Details',
          onClick: () => handleViewOwner(row.id),
          icon: <InfoIcon strokeWidth={1} />
        },
        {
          label: 'Transfer',
          onClick: () => handleTransferCertificate(row),
          // §27 (working-conventions): permission moved out of `visible` into `disabled`/
          // `tooltip` — `!isArchived` stays in `visible` because an archived owner genuinely
          // can't be transferred to, which is a state fact, not a permission gap.
          visible: !isArchived,
          disabled: !isCanTransfer,
          tooltip: isCanTransfer
            ? undefined
            : TRANSFER_PERMISSION_TOOLTIP,
          icon: <ArrowLeftRightIcon strokeWidth={1} />
        },
        {
          label: 'Edit',
          onClick: () => handleEditOwner(row.id),
          visible: !isArchived,
          icon: <PencilIcon strokeWidth={1} />
        },
      ]

      if (!row.workosUserId || row.status === 'pending') {
        items.push({
          label: 'Resend Invitation',
          onClick: () => handleResendInvite(row),
          visible: !isArchived,
          icon: <MailIcon strokeWidth={1} />
        })
      }

      if (isArchived) {
        items.push({
          label: 'Unarchive',
          onClick: () => handleOpenArchiveModal(row, true),
          divider: true,
          icon: <ArchiveRestoreIcon strokeWidth={1} />
        })
      } else {
        items.push({
          label: 'Archive',
          onClick: () => handleOpenArchiveModal(row, false),
          icon: <ArchiveIcon strokeWidth={1} />
        })
        items.push({
          label: 'Delete',
          onClick: () => handleDeleteOwner(row),
          divider: true,
          destructive: true,
          icon: <TrashIcon strokeWidth={1} />
        })
      }

      return items
    },
    [isCanTransfer, showArchived]
  )

  const getBulkActions = useCallback(
    (selectedRows: Owner[]): ActionDropdownItem[] => {
      return [
        {
          label: `Delete ${selectedRows.length} ${selectedRows.length === 1 ? 'Owner' : 'Owners'}`,
          onClick: () => handleBulkDeleteOwners(selectedRows),
          destructive: true,
          icon: <TrashIcon strokeWidth={1} />
        }
      ]
    },
    []
  )

  const hasActiveFilters = useMemo(() => {
    return !!(
      searchParams.get('q') ||
      searchParams.get('gender') ||
      searchParams.get('ageMin') ||
      searchParams.get('ageMax') ||
      searchParams.get('registeredFrom') ||
      searchParams.get('registeredTo') ||
      (searchParams.get('status') && searchParams.get('status') !== 'active')
    )
  }, [searchParams])

  const getEmptyState = useMemo(() => {
    if (total === 0 && !hasActiveFilters) {
      return (
        <EmptyState
          title="No owners added"
          text="Use the Add Owners action above to add owners"
          icon={<UserPlusIcon strokeWidth={1} size={32} />}
        />
      )
    }

    return (
      <EmptyState
        title="No owners found"
        text="No owners match the current filters"
        icon={<UsersIcon strokeWidth={1} size={32} />}
        buttons={<AskAboutButton question="How many owners do I have?" />}
      />
    )
  }, [total, hasActiveFilters])

  return (
    <>
      <Header
        title="People"
        total={tab === 'owners' ? total : undefined}
        rightComponents={
          tab === 'owners' ? (
            <Button
              onClick={handleCreateOwner}
              startIcon={<PlusIcon strokeWidth={1} />}
            >
              Add Owners
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => leadsTabRef.current?.exportCsv()}
              startIcon={<DownloadIcon strokeWidth={1} />}
            >
              Export
            </Button>
          )
        }
      />

      {/*
        VRG-512: sample-data caveat — see ProductsPage's identical comment for the full
        rationale. Placed above the tabs (not just above the Owners table) so it reads as
        a fact about the whole page rather than one tab's table; it costs nothing on the
        Leads tab since it's the same mounted component either way.

        This page's Owners table below DOES use `fillBottom` (§22), unlike Products/
        Certificates — but `useFillBottom` measures `.MuiDataGrid-root`'s own top at
        layout time rather than assuming a fixed offset, so it accounts for this banner's
        height (and the tab row's) automatically; no `topOffset` adjustment needed here.
      */}
      <IntroDataBanner />

      {/*
        `pb: 4` (16px), was `pb: 6` (24px) — VRG-401.

        Measured in the browser rather than reasoned from the token. The tab row's bottom
        to the top of the first content block below it:

          People (before)              40px
          Billing -> Invoices          33px     the like-for-like reference: tabs over a Table
          Billing -> Overview          41px     tabs over a Card (`pt: 4` on that TabPanel)
          People (after)               32px

        Only 8px of the 24 belongs to this padding as far as the eye is concerned — the
        shared `Table` contributes its own 16px of leading underneath it, which is why
        dropping the whole 24 would have overshot to 16px and read tighter than either
        Billing tab. Billing is the reference and is not touched.

        Note the platform's spacing base is 4px (`@repo/ui`, working-conventions §29), so
        this is 16px, not 32. The target is the PIXEL value; the factor is per-app.
      */}
      {/* VRG-470: underline page-header tabs. The hand-tuned `pb: 4` above is replaced by the
          shared TABS_CONTENT_GAP — the measured 32px audit that produced this comment's numbers
          found three different rule-to-content gaps across the app (25 / 34 / 1), so the value
          is now one source rather than per-page. */}
      <Tabs
        redirect={false}
        value={tab}
        onChange={(v) => setTab(v as PeopleTab)}
        tabs={[
          { label: 'Owners', value: 'owners' },
          { label: 'Leads', value: 'leads' },
        ]}
        sx={{ mb: `${TABS_CONTENT_GAP}px` }}
      />

      {tab === 'owners' && (
        <>
          <Table
            dataTour="people-table"
            columns={columns}
            rows={owners}
            loading={isLoading}
            fillBottom
            bulkActions={getBulkActions}
            alwaysShowRowSelection={false}
            showSearch={true}
            search={searchValue}
            showFilters={true}
            showSort={true}
            paginationMode="server"
            rowCount={total ?? -1}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            fetchExportPage={fetchExportPage}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={setRowSelectionModel}
            extraFilters={
              <ToggleField
                label="Show archived owners"
                checked={showArchivedDraft}
                onChange={setShowArchivedDraft}
              />
            }
            extraFiltersDirty={showArchivedDraft !== showArchived}
            onFiltersApply={() => {
              setShowArchived(showArchivedDraft)
              setPaginationModel(prev => ({ ...prev, page: 0 }))
            }}
            // Clear owns the extras too — reset the draft AND the applied value,
            // or the next commit bounces the toggle back on.
            onClearExtraFilters={() => {
              setShowArchivedDraft(false)
              setShowArchived(false)
              setPaginationModel(prev => ({ ...prev, page: 0 }))
            }}
            emptyState={getEmptyState}
            viewKey="owners"
          />

          <DeleteOwnerModal
            owner={selectedOwner}
            open={openDeleteModal}
            onClose={handleCloseDeleteModal}
            refetch={refetch}
          />
          <DeleteOwnersModal
            owners={selectedOwners}
            open={openBulkDeleteModal}
            onClose={handleCloseBulkDeleteModal}
            onDeleted={handleOwnersDeleted}
          />
          <ArchiveOwnerModal
            owner={selectedOwner}
            open={openArchiveModal}
            onClose={handleCloseArchiveModal}
            refetch={refetch}
            unarchiving={isUnarchiving}
          />
          <TransferCertificateModal
            open={openTransferModal}
            owner={selectedOwner ? ownerToCustomer(selectedOwner) : null}
            onClose={handleCloseTransferModal}
            refetch={refetch}
          />
          <AddOwnerModal
            company={company}
            source="owners"
            open={openAddModal}
            createRoute={ROUTES.OWNER_CREATE}
            onClose={handleCloseAddModal}
            refetch={refetch}
          />
        </>
      )}

      {tab === 'leads' && <LeadsTab ref={leadsTabRef} />}
    </>
  )
}
